
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MetricGrowth } from "@/services/analytics/metricsService";
import { Button } from "@/components/ui/button";

interface MetricCardProps {
  title: string;
  value: string | number;
  metric?: MetricGrowth;
  icon?: React.ReactNode;
  tooltip?: string;
  isLoading?: boolean;
  onRefresh?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  metric,
  icon,
  tooltip,
  isLoading = false,
  onRefresh
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={onRefresh}
            >
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          )}
          {icon && <span className="text-muted-foreground">{icon}</span>}
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="h-9 w-full animate-pulse rounded bg-muted"></div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold">
              {tooltip ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help border-b border-dotted border-muted-foreground">
                        {value}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                value
              )}
            </div>
            
            {metric && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 p-1.5 rounded-md bg-slate-50 dark:bg-slate-800 cursor-help">
                      <div className="flex flex-col items-end">
                        <span className={`text-xs font-medium ${
                          metric.trend === "up" 
                            ? "text-emerald-600 dark:text-emerald-500" 
                            : metric.trend === "down" 
                            ? "text-rose-600 dark:text-rose-500" 
                            : "text-gray-500"
                        }`}>
                          {metric.percentage}%
                        </span>
                        <span className="text-[10px] text-muted-foreground">vs. prev.</span>
                      </div>
                      
                      {metric.trend === "up" ? (
                        <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-500" />
                      ) : metric.trend === "down" ? (
                        <TrendingDown className="h-5 w-5 text-rose-600 dark:text-rose-500" />
                      ) : (
                        <Minus className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <div className="space-y-1">
                      <p className="font-medium">Week-over-Week Trend</p>
                      <p className="text-sm">Current period: {metric.currentValue !== undefined ? metric.currentValue : 'N/A'}</p>
                      <p className="text-sm">Previous period: {metric.previousValue !== undefined ? metric.previousValue : 'N/A'}</p>
                      <p className="text-xs text-muted-foreground pt-1">
                        {metric.trend === "up" ? "Increased" : metric.trend === "down" ? "Decreased" : "No change"} by {Math.abs(metric.percentage || 0)}% compared to previous period
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetricCard;
